/**
 * Row shape <-> web Library entry mapping for the shared translations ledger
 * on the baba-v2 mobile gateway (`GET/POST /api/v1/translations*`). See the
 * baba-v2 repo's server/gateway/router.ts, server/gateway/syncRowContract.ts
 * and server/_shared/db/translationsRepo.ts for the wire contract this
 * mirrors, and mobile/services/syncService.ts#mapEntryToDbFormat for the
 * column pairing the phone writes for the same event.
 *
 * Pure and dependency-free (no react, no fetch) so it is unit-testable the
 * same way as ../library-entries.ts.
 */

/** One row as returned by `GET /translations` (`select * from translations`). */
export interface GatewayTranslationRow {
  id: string
  content_type?: string | null
  platform?: string | null
  original_text?: string | null
  translated_text?: string | null
  transliteration?: string | null
  direction?: string | null
  source_language?: string | null
  target_language?: string | null
  from_language?: string | null
  to_language?: string | null
  model_used?: string | null
  translation_mode?: string | null
  gender_context?: string | null
  is_favorite?: boolean | null
  created_at?: string | null
  deleted_at?: string | null
  /** Set for image translations — see server/translate-image's `image_url` column. */
  image_url?: string | null
  /** Set for PDF translations — see server/translate-pdf's `pdf_filename` column. */
  pdf_filename?: string | null
  [key: string]: unknown
}

/**
 * Structurally matches the web app's `HistoryEntry`/`FavoriteEntry`
 * (apps/web/lib/baba-api.ts) plus the local `Entry` type `SavedList`
 * already renders, so gateway-sourced and fallback-sourced rows are
 * interchangeable in the UI.
 */
export interface LibraryEntry {
  id: string
  source_text: string
  translated_text: string
  source_lang: string
  target_lang: string
  gender?: string
  mode?: string
  model?: string
  tags?: string[]
  created_at: string
  /** Only ever set for gateway-sourced entries (fallback favorites/history
   * live in separate tables server-side and carry no such flag). */
  is_favorite?: boolean
  /** 'text' | 'image' | 'pdf' — only ever set for gateway-sourced entries.
   * An image/pdf entry's `source_text` may be empty; the Library opens
   * these in a read-only result view instead of restoring into the
   * translator input. */
  content_type?: string
  /** Original image preview, when the translation was an image and the
   * server recorded a public URL for it. */
  image_url?: string
}

function stringOrEmpty(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function stringOrUndefined(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined
}

/**
 * Maps one gateway row onto the shape the web Library UI already renders.
 * `source_language`/`target_language` and `from_language`/`to_language` are
 * the same pair written twice by every client (legacy column split) — this
 * prefers the `*_language` columns and falls back to `*_language` only when
 * empty, defaulting to en/he like the web's own `fetchFavorites` normalizer.
 */
export function mapGatewayRowToLibraryEntry(row: GatewayTranslationRow): LibraryEntry {
  return {
    id: stringOrEmpty(row.id),
    source_text: stringOrEmpty(row.original_text),
    translated_text: stringOrEmpty(row.translated_text),
    source_lang: stringOrEmpty(row.source_language) || stringOrEmpty(row.from_language) || 'en',
    target_lang: stringOrEmpty(row.target_language) || stringOrEmpty(row.to_language) || 'he',
    gender: stringOrUndefined(row.gender_context),
    mode: stringOrUndefined(row.translation_mode),
    model: stringOrUndefined(row.model_used),
    created_at: stringOrEmpty(row.created_at),
    is_favorite: row.is_favorite === true,
    content_type:
      stringOrUndefined(row.content_type) ||
      (stringOrUndefined(row.image_url) ? 'image' : undefined) ||
      (stringOrUndefined(row.pdf_filename) ? 'pdf' : undefined),
    image_url: stringOrUndefined(row.image_url),
  }
}

/** History = every non-deleted row, in the order the gateway already returns
 * them (`ORDER BY created_at DESC`). */
export function mapGatewayRowsToHistory(rows: GatewayTranslationRow[]): LibraryEntry[] {
  return rows.filter((r) => !r.deleted_at).map(mapGatewayRowToLibraryEntry)
}

/** Saved = every non-deleted row with `is_favorite`, same ordering. */
export function mapGatewayRowsToFavorites(rows: GatewayTranslationRow[]): LibraryEntry[] {
  return rows.filter((r) => !r.deleted_at && r.is_favorite === true).map(mapGatewayRowToLibraryEntry)
}

/**
 * Builds a `POST /translations/sync` row for a text translation just
 * completed (or favorited) on the web, mirroring the columns the phone
 * writes for the same event. The caller supplies `id` — a fresh
 * client-generated UUID, since the gateway silently drops any non-uuid id
 * (see syncRowContract.ts) — so a same-turn "save to favorites" can reuse it
 * to upsert the SAME row instead of creating a second one.
 */
export function buildTextTranslationSyncRow(params: {
  id: string
  sourceText: string
  translatedText: string
  transliteration?: string
  sourceLang: string
  targetLang: string
  mode?: string
  model?: string
  gender?: string
  isFavorite?: boolean
}): Record<string, unknown> {
  const nowIso = new Date().toISOString()
  return {
    id: params.id,
    platform: 'web',
    content_type: 'text',
    original_text: params.sourceText,
    translated_text: params.translatedText,
    transliteration: params.transliteration || null,
    // Matches mobile's mapEntryToDbFormat direction convention.
    direction: params.sourceLang === 'he' ? 'he-to-other' : 'other-to-he',
    source_language: params.sourceLang,
    target_language: params.targetLang,
    from_language: params.sourceLang,
    to_language: params.targetLang,
    model_used: params.model || 'balanced',
    translation_mode: params.mode || 'standard',
    gender_context: params.gender || null,
    is_favorite: params.isFavorite ?? false,
    created_at: nowIso,
    synced_at: nowIso,
  }
}

/**
 * Find the ledger row a translation currently on screen was written as, so
 * favoriting it PATCHES that row instead of pushing a second one.
 *
 * The duplicate-history bug this prevents: the translator only knows a row id
 * for a translation it pushed in this turn. A result restored from
 * localStorage, reopened from History, or one whose push resolved after the
 * click had no id, and the save wrote a fresh row — the saved translation then
 * reappeared at the top of History as if it had just been translated again.
 * Rows arrive newest first, so the first text match is the one being looked at.
 */
export function findLedgerRowForTranslation(
  rows: GatewayTranslationRow[],
  params: { knownId?: string | null; sourceText: string; translatedText: string },
): GatewayTranslationRow | undefined {
  const known = params.knownId
    ? rows.find((row) => row.id === params.knownId && !row.deleted_at)
    : undefined
  if (known) return known
  const source = params.sourceText.trim()
  const translated = params.translatedText.trim()
  if (!translated) return undefined
  return rows.find(
    (row) =>
      !row.deleted_at &&
      (row.translated_text ?? '').trim() === translated &&
      (row.original_text ?? '').trim() === source,
  )
}
